import React, {useContext, useRef, useState, useEffect} from 'react';
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import {useGroup} from "../../../providers/GroupProvider";
import {ThemeContext} from "styled-components";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ButtonPrimary from "./Buttons/ButtonPrimary";
import WithIcons from "./Wrappers/WithIcons";
import { Plus, CaretDown } from '@phosphor-icons/react';

const GroupSelectorCompact = () => {
    const navigate = useNavigate();
    const { selectedGroupId, setSelectedGroup, groups, loading, createGroup } = useGroup();
    const theme = useContext(ThemeContext);
    const formControlRef = useRef(null);
    const selectRef = useRef(null);
    const [menuWidth, setMenuWidth] = useState('auto');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');

    useEffect(() => {
        const updateMenuWidth = () => {
            if (formControlRef.current && groups && groups.length > 0) {
                const rect = formControlRef.current.getBoundingClientRect();
                const buttonWidth = rect.width;

                // Create a temporary canvas to measure text width
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                context.font = '14px Roboto, "Helvetica Neue", Arial, sans-serif'; // body2 variant font

                // Find the longest text width
                let maxTextWidth = 0;
                groups.forEach(group => {
                    const textWidth = context.measureText(group.name).width;
                    maxTextWidth = Math.max(maxTextWidth, textWidth);
                });

                const createNewTextWidth = context.measureText('+ New Group').width;
                const allGroupsTextWidth = context.measureText('All groups').width;
                maxTextWidth = Math.max(maxTextWidth, createNewTextWidth, allGroupsTextWidth);

                // Add padding for menu items (left + right padding)
                const menuItemPadding = 32; // Approximate padding for MenuItem
                const calculatedWidth = Math.max(buttonWidth, maxTextWidth + menuItemPadding);

                setMenuWidth(`${calculatedWidth}px`);
            }
        };


        const handleResize = () => {
            updateMenuWidth();
        };

        updateMenuWidth();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [groups]);

    // No global CSS override - positioning handled via MenuProps

    // Always show the selector, even if no groups exist (because of "Default Group" option)
    if (loading) {
        return null; // Only hide while loading
    }

    const handleChange = (e) => {
        const value = e.target.value;
        if (value === 'create_new') {
            setShowCreateDialog(true);
            return;
        }
        if (value === 'all_groups') {
            setSelectedGroup('all');
            navigate('/dashboard');
            return;
        }
        if (value === 'default_group') {
            setSelectedGroup(null);
            navigate('/dashboard');
            return;
        }
        setSelectedGroup(value === '' ? null : value);
        navigate('/dashboard');
    };


    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;

        try {
            const newGroup = await createGroup(newGroupName.trim());
            if (newGroup) {
                setSelectedGroup(newGroup.id);
                setNewGroupName('');
                setShowCreateDialog(false);
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('Error creating group:', error);
            alert('Error creating group: ' + (error.response?.data?.error || error.message));
        }
    };

    // selectedGroupId: 'all' | null | UUID. Map to Select value and display.
    const effectiveSelectedGroupId = selectedGroupId === 'all' ? 'all_groups' : (selectedGroupId === null ? 'default_group' : selectedGroupId);
    const selectedGroup = selectedGroupId === null || selectedGroupId === 'all' ? null : groups.find(g => g.id === selectedGroupId);

    const getDisplayName = (group, isAllGroups, isDefaultGroupOption) => {
        if (isAllGroups || selectedGroupId === 'all') return 'All groups';
        if (isDefaultGroupOption || selectedGroupId === null) return 'Default Group';
        if (!group) return '';
        return group.name;
    };

    return (
        <>
        <FormControl
            ref={formControlRef}
            size="small"
            sx={{
                minWidth: 100,
                maxWidth: { xs: '40vw', sm: '30vw', md: '20vw' },
                width: '100%',
                mr: 2,
                flexShrink: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.text || 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.text || 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.text || 'rgba(255, 255, 255, 0.5)',
                },
            }}
        >
            <Select
                ref={selectRef}
                value={effectiveSelectedGroupId || ''}
                onChange={handleChange}
                displayEmpty
                renderValue={(value) => {
                    const isAllGroups = value === 'all_groups';
                    const isDefaultGroupOption = value === 'default_group';
                    const displayGroup = isAllGroups || isDefaultGroupOption ? null : groups.find(g => g.id === value);
                    const displayName = getDisplayName(displayGroup, isAllGroups, isDefaultGroupOption);
                    if (!displayName) return null;
                    return (
                        <WithIcons
                            showLeftIcon={true}
                            leftIcon={CaretDown}
                            leftIconSize={18}
                        >
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                    {displayName}
                                </Typography>
                            </Box>
                        </WithIcons>
                    );
                }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    PaperProps: {
                        id: 'group-selector-compact-menu',
                        sx: {
                            width: menuWidth,
                            minWidth: menuWidth,
                        },
                    },
                    TransitionProps: {
                        onEntered: () => {
                            // Position menu below header (48px) - specific to header usage
                            const menuPaper = document.getElementById('group-selector-compact-menu');
                            if (menuPaper && formControlRef.current) {
                                const rect = formControlRef.current.getBoundingClientRect();
                                menuPaper.style.position = 'fixed';
                                menuPaper.style.top = '48px';
                                menuPaper.style.left = `${rect.left}px`;
                            }
                        },
                    },
                }}
                sx={{
                    color: theme.colors.text || '#fff',
                    '& .MuiSelect-icon': {
                        display: 'none', // Hide default dropdown icon
                    },
                    '& .MuiSelect-select': {
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                    },
                }}
            >
                {/* "All groups" option – bill bar shows bills from all groups */}
                <MenuItem value="all_groups" sx={{ pl: 0, pr: 0 }}>
                    <WithIcons>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            All groups
                        </Typography>
                    </WithIcons>
                </MenuItem>
                {/* "Default Group" option for bills without a group */}
                <MenuItem value="default_group" sx={{ pl: 0, pr: 0 }}>
                    <WithIcons>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Default Group
                        </Typography>
                    </WithIcons>
                </MenuItem>
                {groups.filter(g => !g.archived_at).map((group) => {
                    // Display the group's actual name
                    return (
                        <MenuItem
                            key={group.id}
                            value={group.id}
                            sx={{ pl: 0, pr: 0 }}
                        >
                            <WithIcons>
                                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                    {group.name}
                                </Typography>
                            </WithIcons>
                        </MenuItem>
                    );
                })}
                <MenuItem
                    value="create_new"
                    sx={{ pl: 0, pr: 0 }}
                >
                    <WithIcons
                        showLeftIcon={true}
                        leftIcon={Plus}
                        leftIconSize={18}
                    >
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            New Group
                        </Typography>
                    </WithIcons>
                </MenuItem>
            </Select>
        </FormControl>

        <Dialog
            open={showCreateDialog}
            onClose={() => {
                setShowCreateDialog(false);
                setNewGroupName('');
            }}
            PaperProps={{
                sx: {
                    backgroundColor: theme.colors.backgroundPrimary,
                    color: theme.colors.text,
                    minWidth: 320,
                    borderRadius: 2,
                }
            }}
        >
            <DialogTitle
                sx={{
                    color: theme.colors.brandSecondary,
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    textAlign: 'left',
                    pt: 2,
                    pb: 1,
                    px: 2,
                }}
            >
                New Group
            </DialogTitle>
            <DialogContent sx={{ px: 2, pt: 1, pb: 1 }}>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Group Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (newGroupName.trim()) handleCreateGroup();
                            else {
                                setShowCreateDialog(false);
                                setNewGroupName('');
                            }
                        }
                    }}
                    helperText="(Required, max 50 characters)"
                    sx={{
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                            color: theme.colors.text,
                            fontSize: '16px',
                            '& fieldset': {
                                borderColor: theme.colors.border,
                            },
                            '&:hover fieldset': {
                                borderColor: theme.colors.border,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.colors.border,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme.colors.text,
                            fontSize: '16px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '16px',
                        },
                        '& .MuiFormHelperText-root': {
                            color: theme.colors.text,
                        },
                    }}
                />
            </DialogContent>
            <Box sx={{ px: 2, py: 2.5 }}>
                <ButtonPrimary
                    fullWidth
                    onClick={() => {
                        if (newGroupName.trim()) {
                            handleCreateGroup();
                        } else {
                            setShowCreateDialog(false);
                            setNewGroupName('');
                        }
                    }}
                >
                    {newGroupName.trim() ? 'Create' : 'Cancel'}
                </ButtonPrimary>
            </Box>
        </Dialog>
        </>
    );
};

export default GroupSelectorCompact;
